import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const YearOfTheSentenceWritingActivitiesEveryStudent = () => {
  const post = getBlogPost('year-of-the-sentence-writing-activities-every-student');
  return (
    <BlogArticle post={post}>
      <h2>
        {"Only one in three students writes at grade level. That's the starting point."}
      </h2>
      <p>
        {
          "The latest NAEP data paints a clear picture: the majority of students in the U.S. are not writing proficiently. And yet, writing is the skill most tightly linked to reading comprehension, critical thinking, and long-term academic success. When students write, they process what they've learned. When they don't, understanding stays shallow."
        }
      </p>
      <p>
        {
          "That's why literacy leaders are calling 2026 \"the year of the sentence.\" HMH's 2026 trends report highlights sentence-level writing as the year's instructional focus — and the research backs it up. Short, frequent writing helps students retain what they read, build fluency, and develop the confidence to tackle longer compositions. The takeaway? You don't need more essays. You need more sentences."
        }
      </p>
      <h2>{'States are putting money behind it'}</h2>
      <p>
        {
          "This isn't just a trend in education blogs. Multiple states — Michigan, Georgia, California — are mandating literacy improvements and backing them with new funding. Admins are actively looking for tools that support writing across subjects, not just in ELA. If you're a curriculum director reading this, the question isn't whether to invest in writing practice. It's how to make it happen in every classroom, every day, without adding hours of prep to your teachers' plates."
        }
      </p>
      <h2>{'The problem with most AI writing tools'}</h2>
      <p>
        {
          "Here's the paradox: 85% of K-12 teachers report using AI writing tools (Jotform EdTech Trends 2026). But nearly all of them are individual, chatbot-style tools — Grammarly, Khan Writing Coach, ChatGPT. A student sits alone with a screen, types something, and gets feedback from a machine."
        }
      </p>
      <p>
        {
          'That model leaves reluctant writers exactly where they started: staring at a blank screen, alone, with no momentum and no community pulling them in. The students who already write well get a little better. The students who struggle? They stay stuck.'
        }
      </p>
      <p>
        {'Nobody is talking about the other approach — '}
        <strong>{'whole-class, low-barrier writing activities'}</strong>
        {
          " that get every student producing text at the same time. That's the gap. And that's what works."
        }
      </p>
      <h2>{'What works instead: the whole class writes together'}</h2>
      <p>
        {
          'When every student in the room writes at the same time, something shifts. The barrier drops. Reluctant writers get pulled in by the momentum of the room — not singled out, not left behind. You see every response in real time. You spot patterns. You discuss together.'
        }
      </p>
      <p>
        {
          'This connects to two of the learning principles that guide everything Curipod builds:'
        }
      </p>
      <ul>
        <li>
          <strong>{'Engage all learners'}</strong>
          {
            ' — QuickWrites are designed so every student participates, not just the confident writers. The whole class produces text, making the usually-quiet-ones visible.'
          }
        </li>
        <li>
          <strong>{'Student voice and agency'}</strong>
          {
            " — students produce their own text and see their ideas alongside classmates' responses, building ownership of their writing."
          }
        </li>
      </ul>
      <p>
        {
          "It's the opposite of a chatbot conversation. It's a classroom full of voices — written, shared, and discussed."
        }
      </p>
      <h2>{'Activity 1: QuickWrites — five minutes, every student writes'}</h2>
      <p>
        {
          'QuickWrites are 5-minute writing prompts designed to get every student producing text with zero blank-page paralysis. The prompts are fun, low-stakes, and tied to real writing skills — sensory imagery, claims and evidence, brainstorming, descriptive detail.'
        }
      </p>
      <p>
        {"Here's what it looks like: you launch a QuickWrite like "}
        <em>{'"Can You Spot the Fake?"'}</em>
        {
          ' — where students make up a ridiculous problem ("too much ice cream in the world") and write evidence to argue it\'s real. Or '
        }
        <em>{'"You Won a Trip to..."'}</em>
        {
          ' — where students use sensory imagery to describe a road trip destination. Every student writes. Every response is visible. The class votes on favorites and discusses what made the strongest writing stand out.'
        }
      </p>
      <p>
        {
          "The key is the low barrier to entry. Students aren't writing a five-paragraph essay. They're writing for five minutes, seeing their work alongside their classmates', and getting instant AI feedback aligned to the skill they're practicing. That cycle — write, share, discuss, improve — is what builds writing fluency over time."
        }
      </p>
      <p>
        {
          'Teachers can try dozens of ready-made QuickWrite prompts or create their own with custom questions and feedback criteria. Either way, it takes minutes to set up and runs itself.'
        }
      </p>
      <h2>
        {
          'Activity 2: Writing Escape Rooms — gamified writing that pulls reluctant writers in'
        }
      </h2>
      <p>
        {
          'Writing Escape Rooms take a different approach: they wrap writing practice inside a story. Students read, make choices, and write their way through a narrative — and the outcome depends on the quality of their writing.'
        }
      </p>
      <p>
        {'Imagine launching '}
        <em>{'"The Lost Relic"'}</em>
        {
          ' — where students hunt for a missing artifact by writing argumentative paragraphs with claims, evidence, and reasoning. Or '
        }
        <em>{'"Leaving Earth"'}</em>
        {
          ' — where students act as NASA scientists writing informational reports about a new planet. The story creates urgency. The writing is the tool students use to move forward. And instant AI feedback keeps the learning loop tight.'
        }
      </p>
      <p>
        {
          'These activities run 15–20 minutes and come in multiple challenge tiers, so you can use them throughout the year as students grow. For reluctant writers, the game framing is everything — it turns "I don\'t know what to write" into "I need to write my way out."'
        }
      </p>
      <h2>{'What it looks like when every student is writing'}</h2>
      <p>
        {
          "Picture this: you're standing at the front of your classroom. Every student has a device. You've launched a QuickWrite, and within 30 seconds, responses start flowing in. You can see every single one — anonymized, so students feel safe to take risks. The quiet kid in the back row? Writing. The student who usually says \"I don't know\"? Writing."
        }
      </p>
      <p>
        {
          'After five minutes, you pull up a few responses. The class discusses what makes one piece of evidence stronger than another. Students revise. They vote on the most convincing argument. And you have '
        }
        <Link
          href="/c/blog-posts/beyond-the-ela-classroom-how-to-improve-writing-instruction-across-the-curriculum"
          passHref
        >
          <a>{"real data on every student's writing"}</a>
        </Link>
        {' — not from a test, but from a Tuesday morning warm-up.'}
      </p>
      <p>
        {
          "That's what whole-class writing looks like. It's not silent, individual seatwork. It's alive, social, and diagnostic."
        }
      </p>
      <h2>{'Why admins should pay attention right now'}</h2>
      <p>
        {
          "If you're a curriculum director or instructional coach, this matters for a concrete reason: the state literacy mandates that are rolling out right now come with funding. Michigan, Georgia, and California are investing in tools that improve writing across subjects. Districts that can show "
        }
        <Link
          href="/c/blog-posts/from-test-prep-to-lasting-learning-how-to-raise-standardized-test-scores-without-burning-out-teachers-or-students"
          passHref
        >
          <a>{'evidence of daily writing practice and measurable growth'}</a>
        </Link>
        {' are the ones that will justify those investments.'}
      </p>
      <p>
        {
          'Curipod makes this visible. Every QuickWrite and Writing Escape Room generates data — participation rates, writing quality, growth over time. You can see '
        }
        <Link href="/c/blog-posts/46-percent-caasp-growth-ca" passHref>
          <a>{'what happened in Southern California'}</a>
        </Link>
        {
          ", where targeted writing practice with Curipod drove a 46% boost in CAASPP achievement. That's not a fluke. That's what happens when writing practice becomes a daily habit, not a seasonal scramble."
        }
      </p>
      <h2>{'Try it with your next class'}</h2>
      <p>
        {
          "You don't need a new curriculum. You don't need a week of PD. You need five minutes and a QuickWrite."
        }
      </p>
      <p>
        {
          'Launch one tomorrow. Watch every student write. See what they produce. Then decide if this is the writing practice your classroom has been missing.'
        }
      </p>
      <h2>{'Sources'}</h2>
      <ul>
        <li>
          <a
            href={'https://nces.ed.gov/nationsreportcard/'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {"The Nation's Report Card"}
          </a>
          {
            ' — National Assessment of Educational Progress (NAEP), National Center for Education Statistics'
          }
        </li>
        <li>
          <a
            href={'https://www.hmhco.com/blog/current-trends-in-education'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {'10 Top Trends in Education to Watch in 2026'}
          </a>
          {' — HMH'}
        </li>
        <li>
          <a
            href={'https://www.jotform.com/enterprise/whitepapers/edtech-trends-2026/'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {'EdTech Trends 2026'}
          </a>
          {' — Jotform'}
        </li>
        <li>
          <a
            href={
              'https://cdt.org/press/cdt-survey-research-finds-use-of-ai-in-k-12-schools-connected-to-negative-effects-on-students-including-their-real-life-relationships/'
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {'AI in K-12 Schools Connected to Negative Effects on Students'}
          </a>
          {' — Center for Democracy and Technology'}
        </li>
      </ul>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default YearOfTheSentenceWritingActivitiesEveryStudent;
